import React from "react";
import PropTypes from "prop-types";
import { useQuery, useLazyQuery } from "@apollo/react-hooks";
import { useHistory } from "react-router-dom";
import fileDownload from "js-file-download";
import {
  LATEST_TOMO_CREDIT_REPORT,
  LATEST_TOMO_CREDIT_REPORT_DOWNLOAD,
} from "utils";
import { Button, Loading } from "components/common";
import parseError from "utils/parseError";
import Attributes from "./elements/Attributes";

const Results = ({
  attributes,
  downloadLatestTomoCreditReport,
  hasDownloadError,
  downloadErrorMessage,
}) => {
  if (!attributes.length) {
    return <p>No results found</p>;
  }
  return (
    <div>
      <Attributes attributes={attributes} />
      <Button onClick={downloadLatestTomoCreditReport}>Download Details</Button>
      {hasDownloadError && (
        <p className="text-red-600 mt-2 font-semibold">
          {downloadErrorMessage || 'Error downloading report'}
        </p>
      )}
    </div>
  );
};

const CreditReportingDashboard = () => {
  const history = useHistory();
  const {
    loading: reportLoading,
    error: reportError,
    data: reportData,
  } = useQuery(LATEST_TOMO_CREDIT_REPORT);

  const [
    downloadLatestTomoCreditReport,
    { loading: downloadLoading, error: downloadError },
  ] = useLazyQuery(LATEST_TOMO_CREDIT_REPORT_DOWNLOAD, {
    fetchPolicy: "network-only",
    onCompleted: (res) => {
      const {
        latestTomoCreditReportDownload: { csv, reportingMonth, submissionDate },
      } = res;
      fileDownload(
        csv,
        `credit_report_for_${reportingMonth}_submitted_on_${submissionDate}.csv`,
      );
    },
  });

  return (
    <div>
      <div className="my-auto items-center mx-auto justify-center bg-transparent rounded-lg">
        <div className="flex justify-between">
          <h1 className="text-2xl font-normal py-1">
            Credit Reporting Dashboard
          </h1>
          <Button
            green
            onClick={() => history.push("/admin/credit-reporting/search")}
          >
            Search User
          </Button>
        </div>
        {(reportLoading || downloadLoading) && <Loading />}
        {reportError && <p>{parseError(reportError) || 'Error retrieving report'}</p>}
        {reportData && (
          <Results
            attributes={Object.entries(reportData.latestTomoCreditReport || {})}
            downloadLatestTomoCreditReport={downloadLatestTomoCreditReport}
            hasDownloadError={!!downloadError}
            downloadErrorMessage={parseError(downloadError)}
          />
        )}
      </div>
    </div>
  );
};

Results.propTypes = {
  attributes: PropTypes.arrayOf(PropTypes.arrayOf(PropTypes.string)).isRequired,
  downloadLatestTomoCreditReport: PropTypes.func.isRequired,
  hasDownloadError: PropTypes.bool.isRequired,
  downloadErrorMessage: PropTypes.string,
};

Results.defaultProps = {
  downloadErrorMessage: undefined,
};

export default CreditReportingDashboard;
